import { motion } from "framer-motion";
import { GraduationCap } from "lucide-react";

const CONVOCATION_BANNER = `${process.env.PUBLIC_URL}/convocation-login-banner.png`;
const CONVOCATION_BANNER_2X = `${process.env.PUBLIC_URL}/convocation-login-banner@2x.png`;
const LOGO = "/mu-logo.png";

function MicrosoftIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true" className="shrink-0">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export default function ConvocationMicrosoftSignIn({
  title = "Convocation 2026",
  subtitle = "Sign in to access your convocation details",
  description = "Use your university Microsoft or Outlook account — the same email linked to your convocation registration.",
  error,
  onSignIn,
  buttonLabel = "Sign in with Microsoft",
  testId = "convocation-microsoft-signin",
  hideMicrosoftButton = false,
  footer = null,
}) {
  return (
    <div className="min-h-screen bg-white grid grid-cols-1 lg:grid-cols-2" data-testid={testId}>
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden bg-[#B60F2A] p-8 xl:p-12">
        <motion.img
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          src={CONVOCATION_BANNER}
          srcSet={`${CONVOCATION_BANNER} 1024w, ${CONVOCATION_BANNER_2X} 2048w`}
          sizes="(min-width: 1024px) 50vw, 100vw"
          alt="Mahindra University campus"
          className="max-h-[min(92vh,720px)] w-full max-w-[min(96%,920px)] h-auto object-contain rounded-2xl border-4 border-white/90 shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
          decoding="async"
        />
      </div>

      <div className="flex flex-col min-h-screen">
        <div className="lg:hidden p-4 border-b border-[#E5E7EB] bg-[#B60F2A] flex justify-center">
          <img
            src={CONVOCATION_BANNER}
            srcSet={`${CONVOCATION_BANNER} 1024w, ${CONVOCATION_BANNER_2X} 2048w`}
            sizes="100vw"
            alt="Mahindra University campus"
            className="max-h-40 w-full max-w-md object-contain px-2 rounded-xl border-2 border-white/90 shadow-md"
            decoding="async"
          />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <div className="flex items-center gap-3 mb-8">
              <img src={LOGO} alt="Mahindra University" className="h-11 w-auto" />
            </div>

            <div className="inline-flex items-center gap-2 text-xs font-semibold bg-[#FBE7EA] text-[#B60F2A] px-3 py-1.5 rounded-full">
              <GraduationCap className="h-3.5 w-3.5" />
              Mahindra University
            </div>

            <h1 className="mt-5 font-serif text-4xl md:text-5xl font-bold tracking-tight text-[#111] leading-tight">
              {title}
            </h1>
            <p className="text-lg text-[#6B7280] mt-3 font-medium">{subtitle}</p>
            <p className="text-sm text-[#6B7280] mt-3 leading-relaxed">{description}</p>

            {error && (
              <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 leading-relaxed">
                {error}
              </div>
            )}

            {footer || (
              <>
                {!hideMicrosoftButton && (
                  <button
                    type="button"
                    onClick={onSignIn}
                    className="mt-8 w-full h-12 inline-flex items-center justify-center gap-3 rounded-xl border border-[#8C8C8C] bg-white px-4 text-[15px] font-semibold text-[#5E5E5E] shadow-sm hover:bg-[#FAFAFA] hover:border-[#757575] transition-colors"
                    data-testid={`${testId}-button`}
                  >
                    <MicrosoftIcon />
                    {buttonLabel}
                  </button>
                )}

                <p className="text-xs text-[#9CA3AF] mt-6 leading-relaxed text-center">
                  Sign in once to view your registration details, QR passes, and convocation information.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
